import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Ban,
  Check,
  ClipboardCopy,
  FileDown,
  FileText,
  MessageSquareWarning,
  Phone,
  RefreshCcw,
  Send,
  Upload,
  UserCheck,
  UserX,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { CANDIDATE_DECLINE_REASONS, OFFER_STATUS_LABELS, WITHDRAW_JUSTIFICATION_MIN, type OfferDetailView, type OfferStepView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Textarea } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { Popover, usePopover } from '../../../components/Popover';
import { Select } from '../../../components/Select';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { APPROVAL_STEP_TONES, OFFER_STATUS_TONES } from '../../../components/status-tones';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ToneChip, toneClass } from '../../../components/Tone';
import { EMPTY, formatDateTime, formatIsoDate, formatRelative } from '../../../lib/format';
import { offerFiles, useOffer } from '../api/offers-api';
import { OfferTermsForm } from '../components/OfferTermsForm';
import { useOfferActions, type OfferActions } from '../hooks/use-offer-actions';
import { BandNote, money } from '../offer-format';
import styles from '../components/Offers.module.css';
import { Timeline } from '../../communication/components/Timeline';
import { goneId, useRecordVisit } from '../../../lib/use-record-visit';

/** D13: dates and times on an offer read in Bangladesh time, with the zone shown. */
const ZONE = 'Asia/Dhaka';

/**
 * OFR-01 and OFR-03 — one offer, built and then tracked. While it is a draft (or version N+1
 * is being prepared) the builder is open; after that the page tracks it: who approves next,
 * when it lapses, what the candidate answered, every version, and the whole trail. An approver
 * whose step is current decides here — N-48 lands on this page. The hiring manager reads it
 * with every amount `—` (§L23).
 */
export function OfferPage({ offerId }: { readonly offerId: string }): ReactNode {
  const query = useOffer(offerId);
  useRecordVisit(query.data ? { kind: 'offer', id: offerId, title: query.data.candidateName, subtitle: `Offer · ${query.data.jobTitle}`, path: `/offers/${offerId}` } : null, goneId(query.error, offerId));
  if (query.isPending) return <OfferSkeleton />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this offer" contact="Your admin" />;
    if (query.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  return <OfferScreen offer={query.data} />;
}

function OfferScreen({ offer }: { readonly offer: OfferDetailView }): ReactNode {
  const actions = useOfferActions(offer);
  const decidable = offer.steps.find((step) => step.canDecide) ?? null;
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/jobs/$jobId/$tab" params={{ jobId: offer.jobId, tab: 'offers' }} className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          {offer.jobTitle} · offers
        </Link>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <div className={styles.titleLine}>
              <h1 className={styles.title}>Offer for {offer.candidateName}</h1>
              <ToneChip tone={OFFER_STATUS_TONES[offer.status]}>{OFFER_STATUS_LABELS[offer.status]}</ToneChip>
              {offer.isInternalApplicant && (
                <Chip tone="attention" plain>
                  Internal
                </Chip>
              )}
            </div>
            <p className={styles.meta}>
              {[offer.reference, offer.jobTitle, offer.departmentName, offer.ventureName, offer.recruiterName ? `Recruiter ${offer.recruiterName}` : null, offer.hiringManagerName ? `Hiring manager ${offer.hiringManagerName}` : null]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <ActionBar offer={offer} actions={actions} />
        </div>
        <StatusLine offer={offer} />
      </header>

      {decidable && <ApproverPanel offer={offer} step={decidable} actions={actions} />}

      <div className={styles.columns}>
        <div className={styles.main}>
          {offer.can.edit ? <OfferTermsForm offer={offer} onSave={actions.save} /> : <TermsSummary offer={offer} />}
          {offer.missing.length > 0 && (offer.can.edit || offer.status === 'negotiating') && (
            <Card title="Before it can go">
              <ul className={styles.missing}>
                {offer.missing.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Card>
          )}
          <SignedCopy offer={offer} actions={actions} />
          <Timeline subject={{ kind: 'application', id: offer.applicationId }} />
        </div>
        <div className={styles.side}>
          <LetterCard offer={offer} />
          <ApprovalsCard offer={offer} />
          <TimelineCard offer={offer} />
        </div>
      </div>
    </div>
  );
}

// --- What the people on the offer can do next --------------------------------------------------

function ActionBar({ offer, actions }: { readonly offer: OfferDetailView; readonly actions: OfferActions }): ReactNode {
  const { can } = offer;
  const { pending } = actions;
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const navigate = useNavigate();
  const isHired = offer.status === 'accepted' || offer.status === 'joined';
  return (
    <div className={styles.actions}>
      {isHired && (
        <Button variant={offer.status === 'accepted' ? 'solid' : 'outline'} icon={UserCheck} onClick={() => void navigate({ to: '/offers/$offerId/joining', params: { offerId: offer.id } })}>
          {offer.status === 'accepted' ? 'Documents and joining' : 'The hire'}
        </Button>
      )}
      {can.submit && (
        <Button variant="solid" icon={Send} isPending={pending === 'submit'} onClick={actions.submit}>
          Send for approval
        </Button>
      )}
      {can.send && (
        <Button variant="solid" icon={Send} isPending={pending === 'send'} onClick={actions.send}>
          Send to {offer.candidateName.split(/\s+/)[0]}
        </Button>
      )}
      {can.sendRevision && (
        <Button variant="solid" icon={Send} isPending={pending === 'revision'} onClick={actions.sendRevision}>
          Send the revised offer
        </Button>
      )}
      {can.recordAnswer && <AnswerButton offer={offer} actions={actions} />}
      {can.copyMessage && (
        <Button variant="outline" icon={ClipboardCopy} isPending={pending === 'copy'} onClick={actions.copyMessage}>
          Copy message
        </Button>
      )}
      {can.negotiate && (
        <Button variant="outline" icon={RefreshCcw} isPending={pending === 'negotiate'} onClick={actions.negotiate}>
          Renegotiate
        </Button>
      )}
      {can.noShow && (
        <Button variant="outline" icon={UserX} isPending={pending === 'noShow'} onClick={actions.noShow}>
          Didn't join
        </Button>
      )}
      {can.withdraw && (
        <Button variant="danger" icon={Ban} isPending={pending === 'withdraw'} onClick={() => setIsWithdrawing(true)}>
          Withdraw
        </Button>
      )}
      {isWithdrawing && (
        <WithdrawDialog
          offer={offer}
          onClose={() => setIsWithdrawing(false)}
          onConfirm={(text) => {
            setIsWithdrawing(false);
            actions.withdraw(text);
          }}
        />
      )}
    </div>
  );
}

/** F13.07/F13.08 from a call: anchored to its button (CLAUDE.md §5), never centre-screen. */
function AnswerButton({ offer, actions }: { readonly offer: OfferDetailView; readonly actions: OfferActions }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [answer, setAnswer] = useState<'accepted' | 'declined'>('accepted');
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const canRecord = answer === 'accepted' || reasonCode !== null;
  return (
    <>
      <Button ref={triggerRef} variant="outline" icon={Phone} isPending={actions.pending === 'answer'} onClick={toggle} aria-expanded={isOpen}>
        They answered by phone
      </Button>
      <Popover anchor={anchor} onClose={close} label="Record the candidate's answer" width={340}>
        <div className={styles.popoverBody}>
          <p className={styles.popoverTitle}>What did {offer.candidateName.split(/\s+/)[0]} say?</p>
          <Select
            label="Their answer"
            value={answer}
            options={[
              { value: 'accepted', label: 'Accepted the offer' },
              { value: 'declined', label: 'Declined the offer' },
            ]}
            onChange={(value) => setAnswer(value === 'declined' ? 'declined' : 'accepted')}
          />
          {answer === 'declined' && (
            <>
              <Select label="Why" placeholder="Pick a reason" value={reasonCode} options={CANDIDATE_DECLINE_REASONS.map((reason) => ({ value: reason.code, label: reason.label }))} onChange={setReasonCode} />
              <Textarea aria-label="Their words (optional)" rows={2} maxLength={1000} value={note} placeholder="Their words, if they gave any" onChange={(event) => setNote(event.target.value)} />
            </>
          )}
          <p className={styles.hint}>No IP address is kept for an answer given by phone — the record says you entered it.</p>
          <Button
            variant="solid"
            block
            disabled={!canRecord}
            onClick={() => {
              close();
              actions.answer(answer === 'accepted' ? { answer: 'accepted', version: offer.version } : { answer: 'declined', version: offer.version, reasonCode: reasonCode ?? '', note: note.trim() || null });
            }}
          >
            Record the answer
          </Button>
        </div>
      </Popover>
    </>
  );
}

/** F13.11 — destructive and not undoable, so the one centre-screen confirm on this page. */
function WithdrawDialog({ offer, onClose, onConfirm }: { readonly offer: OfferDetailView; readonly onClose: () => void; readonly onConfirm: (justification: string) => void }): ReactNode {
  const [text, setText] = useState('');
  const isSeen = ['sent', 'viewed', 'negotiating', 'accepted'].includes(offer.status);
  const isLongEnough = text.trim().length >= WITHDRAW_JUSTIFICATION_MIN;
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Withdraw the offer for ${offer.candidateName}?`}
      description={isSeen ? `${offer.candidateName} becomes "Not selected". No email goes from the system — tell them yourself.` : `It was never sent, so ${offer.candidateName} goes back to the finalists.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="dangerSolid" disabled={!isLongEnough} onClick={() => onConfirm(text.trim())}>
            Withdraw the offer
          </Button>
        </>
      }
    >
      <Field label="Why" isRequired hint="Kept with the offer, and shown to anyone who opens it.">
        {({ inputId }) => <Textarea id={inputId} rows={3} maxLength={1000} value={text} placeholder="The budget for this seat was frozen." onChange={(event) => setText(event.target.value)} />}
      </Field>
    </Modal>
  );
}

// --- Where it stands --------------------------------------------------------------------------

function StatusLine({ offer }: { readonly offer: OfferDetailView }): ReactNode {
  const current = offer.steps.filter((step) => step.isCurrent);
  const line = statusText(offer, current);
  if (!line) return null;
  return (
    <p className={`${toneClass} ${styles.statusLine}`} data-tone={OFFER_STATUS_TONES[offer.status]}>
      {line}
    </p>
  );
}

function statusText(offer: OfferDetailView, current: readonly OfferStepView[]): string | null {
  const until = offer.validUntil ? formatDateTime(offer.validUntil, { timeZone: ZONE }) : EMPTY;
  switch (offer.status) {
    case 'in_approval':
      return current.length > 0 ? `Waiting on ${current.map((step) => `${step.name} (${step.approverName})`).join(' and ')}.` : 'Waiting for approval.';
    case 'approved':
      return `Approved. Letter version ${offer.currentVersion} is ready — once sent, ${offer.candidateName} has until ${until}.`;
    case 'sent':
      return `Sent ${offer.sentAt ? formatRelative(offer.sentAt) : ''}. Not opened yet. Open until ${until}.`;
    case 'viewed':
      return `Opened ${offer.viewedAt ? formatRelative(offer.viewedAt) : ''}. Open until ${until}.`;
    case 'negotiating':
      return `Renegotiating. ${offer.candidateName}'s link says a revised offer is coming.`;
    case 'accepted':
      return `Accepted ${offer.respondedAt ? formatDateTime(offer.respondedAt, { timeZone: ZONE }) : ''} — version ${offer.acceptedVersionNumber ?? EMPTY}, ${offer.respondedVia === 'link' ? `on the link${offer.responseIpAddress ? ` from ${offer.responseIpAddress}` : ''}` : 'recorded from a call'}. Joining ${formatIsoDate(offer.joiningDate)}.`;
    case 'declined':
      return `Declined: ${offer.declineReasonLabel ?? EMPTY}.${offer.declineNote ? ` “${offer.declineNote}”` : ''}`;
    case 'expired':
      return `Expired on ${until} without an answer.`;
    case 'accepted_no_show':
      return `Accepted, but did not join on ${formatIsoDate(offer.joiningDate)}.`;
    case 'withdrawn_by_company':
      return `Withdrawn: “${offer.statusReason ?? EMPTY}”`;
    default:
      return null;
  }
}

/** An approver whose step is current decides here (N-48 lands on this page). */
function ApproverPanel({ offer, step, actions }: { readonly offer: OfferDetailView; readonly step: OfferStepView; readonly actions: OfferActions }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [reason, setReason] = useState('');
  const terms = offer.approvalTerms;
  return (
    <Card title={`Your approval — ${step.name}`} meta={step.isExtraStep ? 'This step is here because the offer is outside the pay band' : 'Approve, or send it back with a reason'}>
      <div className={styles.approver}>
        {terms && (
          <div className={styles.approverTerms}>
            <span className={styles.gross}>{money(terms.grossMonthlyMinor, terms.currency)} a month</span>
            <span className={styles.meta}>
              Basic {money(terms.baseSalaryMinor, terms.currency)}
              {terms.allowances.map((line) => ` · ${line.label} ${money(line.amountMinor, terms.currency)}`).join('')}
            </span>
            <BandNote position={terms.bandPosition} minMinor={terms.bandMinMinor} maxMinor={terms.bandMaxMinor} currency={terms.currency} isEnforced={offer.isBandEnforced} />
          </div>
        )}
        <div className={styles.actions}>
          <Button variant="solid" icon={Check} isPending={actions.pending === 'approve'} onClick={() => actions.approve(step.id, step.version)}>
            Approve
          </Button>
          <Button ref={triggerRef} variant="outline" icon={MessageSquareWarning} isPending={actions.pending === 'reject'} onClick={toggle} aria-expanded={isOpen}>
            Send back
          </Button>
          {offer.can.previewLetter && (
            <a className={styles.fileLink} href={offerFiles.preview(offer.id)} target="_blank" rel="noreferrer">
              <FileText size={14} aria-hidden /> Read the letter
            </a>
          )}
        </div>
      </div>
      <Popover anchor={anchor} onClose={close} label="Send the offer back" width={340}>
        <div className={styles.popoverBody}>
          <Field label="Why" isRequired hint="The recruiter is told, so they can fix it.">
            {({ inputId }) => <Textarea id={inputId} rows={3} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />}
          </Field>
          <Button
            variant="solid"
            block
            disabled={reason.trim() === ''}
            onClick={() => {
              close();
              actions.reject(step.id, step.version, reason.trim());
            }}
          >
            Send it back
          </Button>
        </div>
      </Popover>
    </Card>
  );
}

// --- The terms, read only ---------------------------------------------------------------------

function TermsSummary({ offer }: { readonly offer: OfferDetailView }): ReactNode {
  const amounts = offer.offer;
  const currency = amounts.currency;
  const isMasked = (amounts.restrictedFields ?? []).includes('grossMonthlyMinor');
  return (
    <Card title="The offer" meta={isMasked ? 'Amounts are shown to the recruiter, HR and the approvers' : 'Monthly figures'}>
      <dl className={styles.facts}>
        <dt>Gross monthly</dt>
        <dd className={styles.gross}>{money(amounts.grossMonthlyMinor, currency)}</dd>
        <dt>Basic</dt>
        <dd>{money(amounts.baseSalaryMinor, currency)}</dd>
        <dt>Allowances</dt>
        <dd>{allowancesText(amounts.allowances, currency)}</dd>
        <dt>Pay band</dt>
        <dd>{amounts.bandPosition && currency ? <BandNote position={amounts.bandPosition} minMinor={amounts.bandMinMinor} maxMinor={amounts.bandMaxMinor} currency={currency} isEnforced={offer.isBandEnforced} /> : EMPTY}</dd>
        <dt>Joining date</dt>
        <dd>{formatIsoDate(offer.joiningDate)}</dd>
        <dt>Open for</dt>
        <dd>{offer.validityDays} days</dd>
        <dt>Probation</dt>
        <dd>{offer.probationMonths === 0 ? 'None' : `${offer.probationMonths} months`}</dd>
        <dt>Notice period</dt>
        <dd>{offer.noticePeriod ?? EMPTY}</dd>
        <dt>Work location</dt>
        <dd>{offer.workLocation ?? EMPTY}</dd>
        <dt>Letter</dt>
        <dd>{offer.letterName ?? EMPTY}</dd>
        <dt>Approved by</dt>
        <dd>{offer.chainName ?? EMPTY}</dd>
      </dl>
    </Card>
  );
}

/** `—` when masked, "None" when there are none (06-qol §11). */
function allowancesText(allowances: OfferDetailView['offer']['allowances'], currency: string | null): string {
  if (allowances === null) return EMPTY;
  if (allowances.length === 0) return 'None';
  return allowances.map((line) => `${line.label} ${money(line.amountMinor, currency)}`).join(' · ');
}

/** D7: the physically signed copy, once the offer is accepted. */
function SignedCopy({ offer, actions }: { readonly offer: OfferDetailView; readonly actions: OfferActions }): ReactNode {
  const input = useRef<HTMLInputElement | null>(null);
  if (!offer.can.uploadSignedCopy && !offer.signedCopy) return null;
  return (
    <Card
      title="Signed copy"
      action={
        offer.can.uploadSignedCopy ? (
          <>
            <input
              ref={input}
              className={styles.hiddenFile}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              aria-label="Choose the signed copy"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) actions.upload(file);
              }}
            />
            <Button variant="outline" size="sm" icon={Upload} isPending={actions.pending === 'upload'} onClick={() => input.current?.click()}>
              {offer.signedCopy ? 'Replace' : 'Add the signed copy'}
            </Button>
          </>
        ) : undefined
      }
    >
      {offer.signedCopy ? (
        <a className={styles.fileLink} href={offerFiles.signedCopy(offer.id)} target="_blank" rel="noreferrer">
          <FileDown size={14} aria-hidden /> {offer.signedCopy.fileName} · added {formatRelative(offer.signedCopy.uploadedAt)}
        </a>
      ) : (
        <p className={styles.quiet}>When the paper copy comes back signed, scan it and add it here. PDF, JPG or PNG, up to 5 MB.</p>
      )}
    </Card>
  );
}

// --- The letter, its versions, its approvals and its trail -------------------------------------

function LetterCard({ offer }: { readonly offer: OfferDetailView }): ReactNode {
  const versions = offer.offer.versions ?? [];
  return (
    <Card
      title="The letter"
      meta={offer.letterName ?? undefined}
      action={
        offer.can.previewLetter ? (
          <a className={styles.fileLink} href={offerFiles.preview(offer.id)} target="_blank" rel="noreferrer">
            <FileText size={14} aria-hidden /> Preview PDF
          </a>
        ) : undefined
      }
    >
      {offer.offer.letterText ? (
        <div className={styles.letter}>{offer.offer.letterText}</div>
      ) : (
        <p className={styles.quiet}>{(offer.offer.restrictedFields ?? []).includes('letterText') ? 'The letter states the salary, so it is shown to the recruiter, HR and the approvers.' : "No letter yet — the job's offer settings choose one."}</p>
      )}
      {versions.length > 0 && (
        <ul className={styles.versions} aria-label="Letter versions">
          {versions.map((version) => (
            <li key={version.number} className={styles.version}>
              {offer.can.downloadPdf ? (
                <a className={styles.fileLink} href={offerFiles.version(offer.id, version.number)} target="_blank" rel="noreferrer">
                  <FileDown size={14} aria-hidden /> Version {version.number}
                </a>
              ) : (
                <span>Version {version.number}</span>
              )}
              <span className={styles.meta}>
                {formatDateTime(version.generatedAt, { timeZone: ZONE })} · {version.changeReason ?? 'Approved'} · {money(version.grossMonthlyMinor, offer.offer.currency)}
              </span>
              {version.isAccepted && <ToneChip tone={OFFER_STATUS_TONES.accepted}>Accepted</ToneChip>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ApprovalsCard({ offer }: { readonly offer: OfferDetailView }): ReactNode {
  if (offer.steps.length === 0) return null;
  return (
    <Card title="Approvals" meta={offer.chainName ?? undefined}>
      <ol className={styles.steps}>
        {offer.steps.map((step) => (
          <li key={step.id} className={styles.step} data-current={step.isCurrent}>
            <div className={styles.stepHead}>
              <span className={styles.stepName}>{step.name}</span>
              <ToneChip tone={APPROVAL_STEP_TONES[step.status]}>{stepLabel(step)}</ToneChip>
            </div>
            <span className={styles.meta}>
              {step.approverName}
              {step.actedByName && step.actedByName !== step.approverName ? ` · decided by ${step.actedByName}` : ''}
              {step.onBehalfOfName ? ` for ${step.onBehalfOfName}` : ''}
              {step.decidedAt ? ` · ${formatDateTime(step.decidedAt, { timeZone: ZONE })}` : ''}
            </span>
            {step.decisionReason && <p className={styles.quote}>“{step.decisionReason}”</p>}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function stepLabel(step: OfferStepView): string {
  if (step.status === 'approved') return 'Approved';
  if (step.status === 'rejected') return 'Sent back';
  if (step.status === 'skipped') return 'Not needed';
  return step.isCurrent ? 'Waiting' : 'Next';
}

function TimelineCard({ offer }: { readonly offer: OfferDetailView }): ReactNode {
  return (
    <Card title="History">
      <ol className={styles.timeline}>
        {[...offer.timeline].reverse().map((entry, index) => (
          <li key={`${entry.at}-${index}`} className={styles.timelineItem}>
            <span className={styles.timelineLabel}>{entry.label}</span>
            <span className={styles.meta}>
              {entry.actorName ?? 'The system'} · {formatDateTime(entry.at, { timeZone: ZONE })}
            </span>
            {entry.detail && <span className={styles.meta}>{entry.detail}</span>}
          </li>
        ))}
      </ol>
    </Card>
  );
}

function OfferSkeleton(): ReactNode {
  return (
    <div className={styles.page}>
      <Skeleton shape="title" width="40%" />
      <SkeletonLines count={2} />
      <SkeletonLines count={8} />
    </div>
  );
}
